/**
 * Convert a date string to relative time format
 * Examples: "Just now", "5 minutes ago", "2 days ago", "3 weeks ago"
 */
export function getRelativeTime(i18n: I18n, dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const secondsAgo = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (secondsAgo < 60) {
        return i18n._(msg({ id: 'date.justNow', message: 'Just now' }));
    }

    const minutesAgo = Math.floor(secondsAgo / 60);
    if (minutesAgo < 60) {
        return i18n._({
            ...msg({ id: 'date.minutesAgo', message: '{count, plural, one {# minute ago} other {# minutes ago}}' }),
            values: { count: minutesAgo },
        });
    }

    const hoursAgo = Math.floor(minutesAgo / 60);
    if (hoursAgo < 24) {
        return i18n._({
            ...msg({ id: 'date.hoursAgo', message: '{count, plural, one {# hour ago} other {# hours ago}}' }),
            values: { count: hoursAgo },
        });
    }

    const daysAgo = Math.floor(hoursAgo / 24);
    if (daysAgo < 7) {
        return i18n._({
            ...msg({ id: 'date.daysAgo', message: '{count, plural, one {# day ago} other {# days ago}}' }),
            values: { count: daysAgo },
        });
    }

    const weeksAgo = Math.floor(daysAgo / 7);
    if (weeksAgo < 4) {
        return i18n._({
            ...msg({ id: 'date.weeksAgo', message: '{count, plural, one {# week ago} other {# weeks ago}}' }),
            values: { count: weeksAgo },
        });
    }

    const monthsAgo = Math.floor(daysAgo / 30);
    if (monthsAgo < 12) {
        return i18n._({
            ...msg({ id: 'date.monthsAgo', message: '{count, plural, one {# month ago} other {# months ago}}' }),
            values: { count: monthsAgo },
        });
    }

    const yearsAgo = Math.floor(daysAgo / 365);
    return i18n._({
        ...msg({ id: 'date.yearsAgo', message: '{count, plural, one {# year ago} other {# years ago}}' }),
        values: { count: yearsAgo },
    });
}

/**
 * Format a date string to a readable format
 * Example: "Jan 15, 2024"
 */
export function formatDate(dateString: string, locale = 'en-US'): string {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
